import {
  ButtonItem,
  CustomRule,
  Form,
  Item,
  Label,
  RequiredRule,
} from "devextreme-react/form";
import { LookupTypes } from "devextreme-react/lookup";
import { Popup } from "devextreme-react/popup";
import notify from "../../lib/notify";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { changePasswordAdmin } from "../../api/auth";
import { downloadUsersToChangePassword } from "../../api/settings";

export const ChangePasswordByAdmin = () => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [userId, setUserId] = useState<number | null>(null);
  const [users, setUsers] = useState<{ id: number; email: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isModalVisible) {
      const fetchUsers = async () => {
        try {
          const response = await downloadUsersToChangePassword();

          if (response.data && Array.isArray(response.data)) {
            setUsers(response.data);
          } else {
            const usersList = response.data.data || [];
            setUsers(usersList);
          }
        } catch (err) {
          notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
        }
      };
      fetchUsers();
    }
  }, [isModalVisible]);

  useEffect(() => {}, [users]);

  const handlePopup = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleSuccessModalClose = () => {
    setIsSuccessModalVisible(false);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      alert(t("Passwords do not match"));
      return;
    }
    if (!userId) {
      alert(t("Please select a user"));
      return;
    }

    setIsLoading(true);
    try {
      const response = await changePasswordAdmin(userId, newPassword);
      if (response.isOk) {
        setIsModalVisible(false);
        notify(t("Password changes succesfull."), "success");
      } else {
        notify(
          `${t("Error:")} ${response.error || t("Unknown error")}`,
          "error"
        );
        setIsModalVisible(false);
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
      setIsModalVisible(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="change-password">
      <h1>{t("Change password users")}</h1>
      <p>
        {t(
          "On this page, you can change the password for your organization's user account"
        )}
      </p>
      <button className="change-password-button" onClick={handlePopup}>
        {t("Change password")}
      </button>

      <Popup
        visible={isModalVisible}
        onHiding={handleCloseModal}
        maxWidth={400}
        height="auto"
        maxHeight="80vh"
        title={t("Change your password")}
        showCloseButton
        dragEnabled
      >
        <Form>
          <Item
            dataField="userId"
            editorType="dxLookup"
            editorOptions={
              {
                items: users,
                displayExpr: "email",
                valueExpr: "id",
                width: 250,
                value: userId,
                onValueChanged: (e: any) => setUserId(e.value),
                placeholder: t("Select a user"),
                dropDownOptions: {
                  height: 300,
                  showScrollbar: "onHover",
                  hideOnOutsideClick: true,
                  showTitle: false,
                },
              } as LookupTypes.Properties
            }
          >
            <RequiredRule message={t("User selection is required")} />
            <Label text={t("User")} />
          </Item>
          <Item
            editorType="dxTextBox"
            editorOptions={{
              value: newPassword,
              onValueChanged: (e: any) => setNewPassword(e.value),
              placeholder: t("Enter your new password"),
              mode: "password",
            }}
          >
            <RequiredRule message={t("New password is required")} />
            <Label text={t("New Password")} />
          </Item>
          <Item
            dataField="confirmPassword"
            editorType="dxTextBox"
            editorOptions={{
              value: confirmPassword,
              onValueChanged: (e: any) => setConfirmPassword(e.value),
              placeholder: t("Confirm your new password"),
              mode: "password",
            }}
          >
            <RequiredRule message={t("Password confirmation is required")} />
            <CustomRule
              message={t("Passwords do not match")}
              validationCallback={() => newPassword === confirmPassword}
            />
            <Label text={t("Confirm new password")} />
          </Item>
          <ButtonItem
            buttonOptions={{
              text: t("Send"),
              type: "default",
              onClick: handleChangePassword,
              disabled: isLoading,
            }}
          />
        </Form>
      </Popup>

      <Popup
        visible={isSuccessModalVisible}
        onHiding={handleSuccessModalClose}
        maxWidth={400}
        height="auto"
        maxHeight="80vh"
        title={t("Password Change Status")}
        showCloseButton
        dragEnabled
      >
        <div className="success-content">
          {error ? (
            <p className="error-message">{error}</p>
          ) : (
            <p>{t("Password change was successful")}</p>
          )}
        </div>
      </Popup>
    </div>
  );
};
